import React, { memo, useMemo } from 'react'

import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { getIn, useFormikContext } from 'formik'

import { Can } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import FadeBetween from '~/src/UI/Shared/FadeBetween'

import { getFieldName } from '../Form/utils'
import AddStaffPopover from '../shared/AddStaffPopover'
import AssigneesList from '../shared/AssigneesList'
import EmptyAssignees from '../shared/EmptyAssignees'

const displayName = 'View/TaskViewAssignees'
const logger = createLogger(displayName)

export const styles = theme => ({
  root: {
    marginTop: theme.spacing(1),
    overflowY: 'auto',
    overflowX: 'hidden',
    borderRadius: theme.spacing(0.75),
    maxHeight: 400,
    '& > button': {
      fontSize: '1rem',
      fontWeight: 'bold'
    }
  },
  assigneesList: {
    backgroundColor: theme.palette.black.disabled,
    borderRadius: theme.spacing(0.75),
    '& [class*=secondaryAction]': {
      marginRight: -7
    }
  }
})

const useStyles = makeStyles(styles)

const TaskViewAssignees = memo(({
  iAmLead,
  namePrefix = null,
  task
}) => {
  const classes = useStyles()
  const formikContext = useFormikContext()
  const { setFieldValue, setValues, values } = formikContext
  const assignedFieldName = getFieldName('assigned', namePrefix)
  const assigneesFieldName = getFieldName('assignees', namePrefix)
  const assignees = getIn(values, assigneesFieldName)
  const lead = getIn(values, assignedFieldName)
  const { completedOn, inflight } = task
  const disabled = useMemo(() => Boolean(completedOn || inflight === 'save'), [completedOn, inflight])

  return (
    <FadeBetween
      when={Boolean(assignees?.length && lead)}
      then={(
        <>
          <div className={classes.root}>
            <div className={classes.assigneesList}>
              <AssigneesList
                aria-label="Multiple assignees list"
                assignees={assignees}
                disabled={disabled}
                iAmLead={iAmLead}
                lead={lead}
                namePrefix={namePrefix}
                setLead={membershipId => setFieldValue(assignedFieldName, membershipId, true)}
              />
            </div>
          </div>
          <Can any actions={['change_task', iAmLead]}>
            <Box
              position="sticky"
              bottom={0}
              left={0}
            >
              <AddStaffPopover
                addStaffLabel
                assignees={assignees}
                disabled={disabled}
                namePrefix={namePrefix}
                setValues={setValues}
              />
            </Box>
          </Can>
        </>
      )}
      otherwise={(
        <EmptyAssignees
          assignees={assignees}
          disabled={disabled}
          completedOn={completedOn}
          namePrefix={namePrefix}
          setValues={setValues}
        />
      )}
    />
  )
})

TaskViewAssignees.displayName = displayName

export default TaskViewAssignees
